import React from "react";
import { Download } from "lucide-react";

export interface ExportCsvButtonProps {
  /** Called when the button is clicked. */
  onClick: () => void;
  /** Disables the button and reduces opacity when true. */
  disabled?: boolean;
  /** Button label. Defaults to "Export CSV". */
  label?: string;
  /** data-testid for the button element. Defaults to "button-export-csv". */
  "data-testid"?: string;
  /** Additional inline styles merged into the button's style prop. */
  style?: React.CSSProperties;
}

/**
 * ExportCsvButton
 *
 * A small dark-themed button with a download icon for triggering CSV exports.
 * Works without any CSS framework — uses inline styles.
 *
 * @example
 * <ExportCsvButton onClick={handleExport} label="Download Report" />
 */
export function ExportCsvButton({
  onClick,
  disabled,
  label = "Export CSV",
  "data-testid": testId = "button-export-csv",
  style,
}: ExportCsvButtonProps) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: "#111118",
        border: "1px solid #ffffff15",
        color: "#e2e8f0",
        borderRadius: 8,
        padding: "7px 13px",
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap" as const,
        ...style,
      }}
    >
      <Download style={{ width: 13, height: 13, flexShrink: 0 }} />
      <span>{label}</span>
    </button>
  );
}
